/* ILS — Shared "email popup / inline form" RESULT renderer. TASK-2432.
   Consumed by custom-email-popup.js (popup) and custom-email-forms.js
   (footer + homepage inline forms) so the after-submit screen is the same
   component in all three surfaces — same 5 states, same texts, same
   copy-to-clipboard behavior, same date formatting.

   Public API (attached to window):

     window.ILSEmailResult.render(container, data, opts?)
       container : HTMLElement  — target node whose innerHTML is replaced.
       data      : { status, code?, expires_at? }
                   status ∈ { 'new', 'existing_no_order',
                              'existing_one_order',
                              'existing_multiple_orders', 'error' }
       opts      : { variant?: 'popup'|'inline' } — reserved for
                   variant-specific tweaks (unused today; kept so
                   callers don't have to change if we ever fork visuals).

     window.ILSEmailResult.formatDate(iso) → 'DD.MM.YYYY'  (empty string
                   on bad / missing input).

   States 1-3 render a promo code + "Valid for 30 days (until DD.MM.YYYY)".
   State 4 (existing_multiple_orders) renders recognition copy, no code.
   State 5 (error) renders a generic try-again message. Callers decide
   when to pass state 5 — this module never invents it.
*/
(function () {
  'use strict';

  /* TASK-5998 — funnel telemetry emitter. Fail-silent and order-independent:
     if assets/custom-funnel-telemetry.js has not executed yet the call is
     queued on window.ILSFunnelQ and drained when it does; if it never loads
     at all (404, blocked, throwing) every call here is a no-op and this form
     behaves exactly as it did before TASK-5998. It NEVER sends an address or a
     code value. */
  function ilsFn(stage, outcome, props) {
    try {
      if (window.ILSFunnel && window.ILSFunnel.event) {
        window.ILSFunnel.event(stage, outcome, props);
        return;
      }
      (window.ILSFunnelQ = window.ILSFunnelQ || []).push([stage, outcome, props]);
    } catch (e) {}
  }
  function ilsFnCtx(form, c) {
    try { if (window.ILSFunnel && window.ILSFunnel.ctx) window.ILSFunnel.ctx(form, c); } catch (e) {}
  }
  function ilsFnAttempt(form) {
    try {
      if (window.ILSFunnel && window.ILSFunnel.attempt) return window.ILSFunnel.attempt(form);
    } catch (e) {}
    return null;
  }
  function ilsFnObserve(el, form) {
    try { if (window.ILSFunnel && window.ILSFunnel.observeImpression) window.ILSFunnel.observeImpression(el, form); } catch (e) {}
  }

  /* TASK-5998 — WHICH FORM IS THIS PANEL IN? Derived from the DOM rather than
     from a new argument, so none of the three call sites has to change and a
     CDN-cached older build of any of them still reports correctly. The two
     inline forms mark their container with data-ils-inline-source; the popup's
     result node lives inside [data-ils-email-popup]. */
  function funnelForm(container) {
    try {
      var el = container;
      while (el && el !== document.body) {
        if (el.hasAttribute && el.hasAttribute('data-ils-inline-source')) {
          return (el.getAttribute('data-ils-inline-source') || '').toLowerCase() || null;
        }
        if (el.hasAttribute && el.hasAttribute('data-ils-email-popup')) return 'popup';
        el = el.parentNode;
      }
    } catch (e) {}
    return null;
  }

  function formatDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    var dd = String(d.getDate()).padStart(2, '0');
    var mm = String(d.getMonth() + 1).padStart(2, '0');
    var yyyy = d.getFullYear();
    return dd + '.' + mm + '.' + yyyy;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  /* TASK-2603 — localized strings for the after-submit result screen. Read the
     JSON emitted once globally by sections/custom-email-popup.liquid
     (data-ils-eresult-i18n; that popup is included on every page via
     layout/theme.liquid, so the block is present wherever the result renders —
     popup, footer form, homepage form). English defaults keep zero regression
     if the block is missing/unparseable. {date} in untilTpl is filled by eFmt. */
  var _eI18nCache = null;
  function eI18n() {
    if (_eI18nCache) return _eI18nCache;
    var d = {
      titleNew: "You're in!",
      codeLabel: 'Code:',
      titleNoOrder: "You're already in our database, just haven't ordered yet",
      hereCode: "Here's your code:",
      titleOneOrder: "You're already with us and made your first order",
      here5off: "Here's 5% off your second order",
      titleMulti: "This offer is for first orders, and you've already made 2 or more",
      thanksRegular: 'Thank you for being a regular customer',
      watchInbox: 'Watch your inbox for special offers we send our loyal customers',
      errorTitle: 'Something went wrong',
      errorBody: 'Please try again in a moment',
      valid30: 'Valid for 30 days',
      untilTpl: '(until {date})',
      copied: 'Copied'
    };
    var found = false;
    try {
      var el = document.querySelector('[data-ils-eresult-i18n]');
      if (el && el.textContent) {
        var p = JSON.parse(el.textContent);
        for (var k in p) { if (p[k]) { d[k] = p[k]; } }
        found = true;
      }
    } catch (e) { /* keep English defaults */ }
    if (found) { _eI18nCache = d; }
    return d;
  }
  function eFmt(tpl, vars) {
    return String(tpl).replace(/\{(\w+)\}/g, function (m, key) {
      return (vars && vars[key] != null) ? vars[key] : '';
    });
  }

  function copyToClipboard(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      try {
        var ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.top = '-1000px';
        document.body.appendChild(ta);
        ta.select();
        var ok = document.execCommand('copy');
        document.body.removeChild(ta);
        ok ? resolve() : reject(new Error('execCommand failed'));
      } catch (e) { reject(e); }
    });
  }

  function bindCopy(btn, flashEl) {
    btn.addEventListener('click', function () {
      var code = btn.getAttribute('data-code') || btn.textContent.trim();
      copyToClipboard(code).then(function () {
        flashEl.classList.add('is-visible');
        setTimeout(function () { flashEl.classList.remove('is-visible'); }, 1500);
      }, function () {
        // Silent — nothing else we can do. User can still select manually.
      });
    });
  }

  function codeBlockHtml(code, expiresAt) {
    var dateStr = formatDate(expiresAt);
    var t = eI18n();
    return (
      '<div class="ils-eresult__code-row">' +
        '<button type="button" class="ils-eresult__code" data-ils-copy data-code="' + esc(code) + '" aria-label="Copy code ' + esc(code) + '">' +
          esc(code) +
        '</button>' +
        '<span class="ils-eresult__copied" data-ils-copied aria-live="polite">' + esc(t.copied) + '</span>' +
      '</div>' +
      '<p class="ils-eresult__valid">' + esc(t.valid30) + ' ' +
        '<span class="ils-eresult__until">' + esc(eFmt(t.untilTpl, { date: dateStr })) + '</span>' +
      '</p>'
    );
  }

  function render(container, data, opts) {
    if (!container) return;
    opts = opts || {};
    data = data || {};
    var status = data.status || 'error';
    var expires = data.expires_at || data.expiresAt;
    var code = data.code || '';
    // TASK-5839 — REPORT-5836 C-2, fixed at the last possible moment so it
    // holds for every caller and every future response shape: a state that
    // renders a code block but has no code degrades to the error state.
    // Rendering it as-is is what printed an empty copy button and the
    // string "Valid for 30 days ()" to real shoppers.
    if (!code && status !== 'existing_multiple_orders' && status !== 'error') {
      status = 'error';
    }
    var body;
    var t = eI18n();

    if (status === 'new') {
      body =
        '<p class="ils-eresult__title">' + esc(t.titleNew) + '</p>' +
        '<p class="ils-eresult__intro">' + esc(t.codeLabel) + '</p>' +
        codeBlockHtml(code, expires);
    } else if (status === 'existing_no_order') {
      body =
        '<p class="ils-eresult__title">' + esc(t.titleNoOrder) + '</p>' +
        '<p class="ils-eresult__intro">' + esc(t.hereCode) + '</p>' +
        codeBlockHtml(code, expires);
    } else if (status === 'existing_one_order') {
      body =
        '<p class="ils-eresult__title">' + esc(t.titleOneOrder) + '</p>' +
        '<p class="ils-eresult__intro">' + esc(t.here5off) + '</p>' +
        '<p class="ils-eresult__intro">' + esc(t.codeLabel) + '</p>' +
        codeBlockHtml(code, expires);
    } else if (status === 'existing_multiple_orders') {
      body =
        '<p class="ils-eresult__title">' + esc(t.titleMulti) + '</p>' +
        '<p class="ils-eresult__intro">' + esc(t.thanksRegular) + ' <span class="ils-eresult__heart">🧡</span></p>' +
        '<p class="ils-eresult__intro">' + esc(t.watchInbox) + '</p>';
    } else {
      body =
        '<p class="ils-eresult__title">' + esc(t.errorTitle) + '</p>' +
        '<p class="ils-eresult__intro">' + esc(t.errorBody) + '</p>';
    }

    container.innerHTML =
      '<div class="ils-eresult ils-eresult--' + status + '" data-ils-eresult-status="' + status + '">' +
        body +
      '</div>';

    var copyBtn = container.querySelector('[data-ils-copy]');
    var copiedEl = container.querySelector('[data-ils-copied]');
    if (copyBtn && copiedEl) bindCopy(copyBtn, copiedEl);

    /* -----------------------------------------------------------------
       TASK-5998 — THE MOMENT A CODE IS ACTUALLY PAINTED, OR NOT.

       This function is the single renderer for all three forms, so one hook
       here covers every surface and cannot disagree with itself. It is also
       the only place that knows the TRUTH: the status has already been degraded
       to 'error' above when a success state arrived without a code (the
       REPORT-5836 C-2 defect), so 'code_shown' means a real code is on the
       page — not "the API returned 200".

       The code VALUE is never sent. Only whether one was painted, and how
       long it was, which is enough to tell a real code from an empty string
       without putting a coupon in an analytics table.
       ----------------------------------------------------------------- */
    try {
      var form = funnelForm(container);
      var painted = !!(copyBtn && (copyBtn.getAttribute('data-code') || '').length > 0);
      var outcome = painted ? 'code_shown'
        : (status === 'existing_multiple_orders' ? 'recognised_no_code' : 'error');
      ilsFn('client_result', outcome, {
        form: form || undefined,
        now: true,
        /* the interaction has terminated: the next submit on this page is a
           NEW attempt, so a shopper who tries twice is two attempts. */
        endAttempt: true,
        reason: status,
        detail: {
          status: status,
          has_code: painted,
          code_length: painted ? (copyBtn.getAttribute('data-code') || '').length : 0,
          variant: (opts && opts.variant) || null
        }
      });
    } catch (e) {}
  }

  window.ILSEmailResult = { render: render, formatDate: formatDate };
})();
